import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Save, Phone, Mail, MapPin, MessageSquare } from 'lucide-react';
import { adminGetSettings, adminUpdateSettings } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

export default function AdminSettings() {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [settings, setSettings] = useState({
    phone: '',
    email: '',
    slogan: '',
    address: '',
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await adminGetSettings();
        const settingsMap = {};
        res.data.forEach(s => {
          settingsMap[s.key] = s.value;
        });
        setSettings({
          phone: settingsMap.phone || '',
          email: settingsMap.email || '',
          slogan: settingsMap.slogan || '',
          address: settingsMap.address || '',
        });
      } catch (err) {
        console.error('Error fetching settings:', err);
        toast.error('Gabim gjatë marrjes së konfigurimeve');
      } finally {
        setIsLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const settingsArray = Object.entries(settings).map(([key, value]) => ({
        key,
        value: value || '',
      }));
      await adminUpdateSettings(settingsArray);
      toast.success('Konfigurimet u ruajtën me sukses!');
    } catch (err) {
      console.error('Error saving settings:', err);
      toast.error('Gabim gjatë ruajtjes së konfigurimeve');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-settings-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[#0A0A0A]">Konfigurimet</h1>
        <p className="text-[#71717A]">Menaxho informacionet e kontaktit dhe të kompanisë</p>
      </div>

      <div className="max-w-2xl">
        <form onSubmit={handleSubmit} className="bg-white p-8 rounded-sm border border-[#E4E4E7]">
          <div className="space-y-6">
            {/* Phone */}
            <div>
              <Label htmlFor="phone" className="flex items-center gap-2 mb-2">
                <Phone size={16} className="text-[#D4B982]" />
                Numri i Telefonit
              </Label>
              <Input
                id="phone"
                type="tel"
                value={settings.phone}
                onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                placeholder="0176 34681594"
                data-testid="settings-phone"
              />
              <p className="text-xs text-[#71717A] mt-1">
                Do të shfaqet në header dhe footer të faqes
              </p>
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="email" className="flex items-center gap-2 mb-2">
                <Mail size={16} className="text-[#D4B982]" />
                Adresa E-mail
              </Label>
              <Input
                id="email"
                type="email"
                value={settings.email}
                onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                placeholder="info-adservice@gmx.de"
                data-testid="settings-email"
              />
            </div>

            {/* Slogan */}
            <div>
              <Label htmlFor="slogan" className="flex items-center gap-2 mb-2">
                <MessageSquare size={16} className="text-[#D4B982]" />
                Slogani
              </Label>
              <Input
                id="slogan"
                type="text"
                value={settings.slogan}
                onChange={(e) => setSettings({ ...settings, slogan: e.target.value })}
                placeholder="Zuverlässig, schnell und sauber."
                data-testid="settings-slogan"
              />
              <p className="text-xs text-[#71717A] mt-1">
                Slogani i kompanisë që shfaqet në faqe (gjermanisht)
              </p>
            </div>

            {/* Address */}
            <div>
              <Label htmlFor="address" className="flex items-center gap-2 mb-2">
                <MapPin size={16} className="text-[#D4B982]" />
                Adresa
              </Label>
              <Input
                id="address"
                type="text"
                value={settings.address}
                onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                placeholder="Deutschland"
                data-testid="settings-address"
              />
            </div>

            {/* Submit Button */}
            <div className="pt-4 border-t border-[#E4E4E7]">
              <Button
                type="submit"
                disabled={isSaving}
                className="bg-[#D4B982] hover:bg-[#C5A059] text-[#0A0A0A] font-semibold"
                data-testid="settings-submit"
              >
                {isSaving ? (
                  'Duke ruajtur...'
                ) : (
                  <>
                    <Save size={18} className="mr-2" />
                    Ruaj Konfigurimet
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>

        {/* Info Card */}
        <div className="mt-6 p-6 bg-[#F4F4F5] rounded-sm">
          <h3 className="font-semibold text-[#0A0A0A] mb-2">Informacion</h3>
          <p className="text-sm text-[#71717A]">
            Këto konfigurime përdoren në të gjithë faqen publike. Ndryshimet ruhen automatikisht 
            dhe shfaqen menjëherë në faqen kryesore.
          </p>
        </div>
      </div>
    </div>
  );
}
